import MobileAppBar from '@/components/molecules/MobileAppBar';
import React, { useState, useEffect } from 'react';
import searchIcon from '@/assets/icons/search.svg';
import InvoiceIcon from '@/assets/icons/invoice.svg';
import TabPills from '@/components/molecules/TabPills';
import noInvoiceIcon from '@/assets/icons/no-invoice.svg';
import { Button } from '@/components/ui/button';
import { invoiceService } from '@/services/invoice.service';
import { Invoice } from '@/services/types/invoice.types';
import { useNavigate, useParams } from 'react-router-dom';
import { PAGE } from '@/routes';
import { setBreadcrumbs } from '@/store/slices/breadcrumb';
import { useDispatch } from 'react-redux';
import { Budget } from '@/services/types/budgets.types';
import toast from 'react-hot-toast';
import Pagination from '@/components/molecules/Pagination';
import { PlusIcon, X } from 'lucide-react';
import InvoiceOverviewSidebar from '@/components/organisms/invoice/InvoiceOverviewSidebar';
import InvoicesTable from '@/components/molecules/invoice/InvoicesTable';
import { useWidth } from '@/hooks/useWidth';
import isMobile from 'is-mobile';

const InvoicePage = () => {
  const [activeTab, setActiveTab] = useState('All');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<(Invoice & { budget?: Budget }) | null>(null);
  const [statusTab, setStatusTab] = useState('waiting');
  const isDraftView = statusTab === 'draft';

  const width = useWidth();
  const { budgetId } = useParams();
  const isMobileView = width < 768 || isMobile() ? true : false;
  console.log('isMobileView', isMobileView);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    // Sets breadcrumbs when component mounts
    dispatch(
      setBreadcrumbs(
        budgetId
          ? [
              {
                title: 'Budget',
                path: PAGE.BUDGETS.path + '/' + budgetId
              },
              {
                title: 'Invoices',
                path: PAGE.INVOICE.path
              }
            ]
          : [
              {
                title: 'Invoices',
                path: PAGE.INVOICE.path
              }
            ]
      )
    );
  }, [dispatch]);

  useEffect(() => {
    setLoadingInvoices(true);
    invoiceService
      .getInvoices({
        page: currentPage,
        limit: perPage,
        invoice_category: activeTab === 'All' ? undefined : activeTab.toUpperCase(),
        type: statusTab,
        budget_id: budgetId || undefined
      })
      .then((res) => {
        setInvoices(res.data.data || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalCount(res.data.totalCount || 0);
        setCurrentPage(res.data.currentPage || 1);
      })
      .catch((error) => {
        toast.error('Error fetching invoices');
      })
      .finally(() => {
        setLoadingInvoices(false);
      });
  }, [currentPage, perPage, activeTab, statusTab, budgetId]);

  const handleCreateInvoiceClick = () => {
    if (budgetId) {
      navigate(`${PAGE.INVOICE.path}/${PAGE.INVOICE.children.CREATE}`, {
        state: { from: 'budget', budgetId }
      });
    } else {
      navigate(PAGE.INVOICE.children.CREATE, { state: { from: 'invoice' } });
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePerPageChange = (perPage: number) => {
    setPerPage(perPage);
  };

  const handleInvoiceSelect = (invoiceData: Invoice & { budget?: Budget }) => {
    if (isMobileView) {
      navigate(`${PAGE.INVOICE.path}/${invoiceData._id}`, { state: { budget: invoiceData.budget } });
      return;
    }
    if (isDraftView) {
      navigate(`${PAGE.INVOICE.path}/${PAGE.INVOICE.children.CREATE}`, {
        state: { from: 'draft', invoiceId: invoiceData._id }
      });
      return;
    }

    setSelectedInvoice(invoiceData);
  };

  useEffect(() => {
    if (isDraftView && selectedInvoice) {
      setSelectedInvoice(null);
    }
  }, [isDraftView, selectedInvoice]);

  return (
    <>
      <MobileAppBar title="Invoice" variant="primary" onRightIconClick={() => {}} rightIcon={searchIcon} />
      <div className="flex-1 flex gap-6 w-full max-w-[1440px] mx-auto overflow-hidden">
        {/* main */}
        <div className="flex-1 flex flex-col gap-4">
          <div className="flex-1 bg-white w-full md:rounded-[16px] p-0 pt-1 md:p-4 border flex flex-col gap-4">
            <header className="hidden md:flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-2xl font-semibold">
                <img src={InvoiceIcon} alt="invoice" className="w-6 h-6" />
                <span>Recent Invoice</span>
              </h2>
            </header>

            {/* Statsu Tabs */}
            <div className="flex border-b items-center gap-4">
              <button
                className={`text-sm md:text-base font-medium px-4 md:px-0 py-2 border-b-2 ${statusTab === 'waiting' ? 'border-primary font-medium' : 'border-transparent'} `}
                onClick={() => setStatusTab('waiting')}
              >
                Waiting
              </button>
              <button
                className={`text-sm md:text-base font-medium px-4 md:px-0 py-2 border-b-2 ${statusTab === 'validated' ? 'border-primary font-medium' : 'border-transparent'} `}
                onClick={() => setStatusTab('validated')}
              >
                Validated
              </button>
              <button
                className={`text-sm md:text-base font-medium px-4 md:px-0 py-2 border-b-2 ${statusTab === 'draft' ? 'border-primary font-medium' : 'border-transparent'} `}
                onClick={() => setStatusTab('draft')}
              >
                Draft
              </button>
            </div>

            <div className="flex items-center justify-between gap-3 px-4 md:px-0">
              <TabPills options={['All', 'Expense', 'Revenue']} value={activeTab} onValueChange={setActiveTab} />
              <Button
                variant={'text'}
                onClick={handleCreateInvoiceClick}
                className="rounded-full hidden md:flex items-center font-semibold text-sm md:text-base gap-1 px-6"
              >
                <PlusIcon className="w-6 h-6" />
                <span>Create an Invoice</span>
              </Button>
            </div>

            <div className="flex-1 flex overflow-y-auto max-h-[calc(100vh-270px)] md:max-h-none">
              {loadingInvoices || invoices.length > 0 ? (
                <div className="flex-1 flex px-4 md:px-0 mt-2 md:mt-0">
                  <InvoicesTable
                    invoices={invoices}
                    setSelectedInvoice={setSelectedInvoice}
                    isDraft={isDraftView}
                    onInvoiceSelect={handleInvoiceSelect}
                    loading={loadingInvoices}
                  />
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center flex-col gap-4">
                  <div className="flex flex-col items-center gap-2">
                    <img className="w-[80px] h-[80px]" src={noInvoiceIcon} alt="no-invoice-icon" />
                    <div className="flex flex-col items-center justify-center">
                      <p className="font-medium leading-[140%]">There are no Invoice to show</p>
                      <span className="text-secondary text-sm leading-[125%]">
                        Please create a new invoice to allocate budget!
                      </span>
                    </div>
                  </div>
                  <Button onClick={handleCreateInvoiceClick} variant={'primary-reverse'} className="rounded-full px-6">
                    Create an Invoice
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* pagination */}
          <div className="hidden md:block">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              loading={loadingInvoices}
              perPage={perPage}
              onPerPageChange={handlePerPageChange}
              totalCount={totalCount}
            />
          </div>
        </div>

        {/* sidebar */}
        {selectedInvoice && !isDraftView && (
          <InvoiceOverviewSidebar
            invoice={selectedInvoice}
            onClose={() => setSelectedInvoice(null)}
            budgetId={budgetId}
          />
        )}
      </div>
    </>
  );
};

export default InvoicePage;
